/**
 * @ken/module-dispatch — frontend entry point (PRD 47 Task 2).
 *
 * Routes, static before parameterised (`/dispatch/new` wins over `/dispatch/:id`):
 *   /dispatch        the Jobs list (`?filter=` and `?q=` in the address; a job
 *                    opens in the record pop-up over it, ADR-0229)
 *   /dispatch/new    New job
 *   /dispatch/tickets  Tickets with no job (PRD 48 section 7; `?filter=`)
 *   /dispatch/signoff-people  Sign-off people (SS-B4-T2): the company's one list
 *                    of people who approve its work, and the customers each serves
 *   /dispatch/requests  Requests for work (LOG-D-T16; `?state=`), /requests/new
 *                    (the office's own) and /requests/$requestId (one request)
 *   /dispatch/$jobId one job on its own page
 *   /dispatch/$jobId/edit  the job's edit form (LOG-D-T7, E-1): the New job
 *                    form in edit mode
 */

import { lazyScreen, type AnyRoute, type CreateModuleRoutes } from "@ken/cockpit-types";
import { isRecordId, RecordNotFound } from "@ken/ui";
import { createRoute, notFound } from "@tanstack/react-router";

import { validateFromRequestSearch, validateJobsSearch, validateRequestsSearch, validateTicketsSearch } from "./search";

// One loader per screen file, shared by every screen that file exports, so the
// route table carries each file's import (and its preload list) once (SF-BUNDLE).
const loadPages = () => import("./pages");
const loadRequests = () => import("./Requests");

// Screens download the first time they are opened (ADR-040, BND-01).
const DispatchJobsPage = lazyScreen(loadPages, "DispatchJobsPage");
const DispatchNewJobPage = lazyScreen(loadPages, "DispatchNewJobPage");
const DispatchJobPage = lazyScreen(loadPages, "DispatchJobPage");
const DispatchEditJobPage = lazyScreen(loadPages, "DispatchEditJobPage");
const DispatchTicketsPage = lazyScreen(() => import("./Tickets"), "DispatchTicketsPage");
const DispatchRequestsPage = lazyScreen(loadRequests, "DispatchRequestsPage");
const DispatchRequestPage = lazyScreen(loadRequests, "DispatchRequestPage");
const DispatchNewRequestPage = lazyScreen(loadRequests, "DispatchNewRequestPage");
const DispatchSignoffPeoplePage = lazyScreen(() => import("./SignoffPeoplePage"), "DispatchSignoffPeoplePage");

/** SS-B4-T2: the sign-off people screen, a static path under /dispatch (before $jobId). */
const SIGNOFF_PEOPLE = { path: "signoff-people", component: DispatchSignoffPeoplePage } as const;

export const recordTabs = {
  DispatchRecordJobsTab: lazyScreen(loadPages, "DispatchRecordJobsTab"),
  DispatchCustomerTermsTab: lazyScreen(() => import("./CustomerTerms"), "DispatchCustomerTermsTab"),
};

export const createRoutes: CreateModuleRoutes = (parentRoute: AnyRoute) => {
  const list = createRoute({
    getParentRoute: () => parentRoute,
    path: "dispatch",
    validateSearch: validateJobsSearch,
    component: DispatchJobsPage,
  });
  const create = createRoute({
    getParentRoute: () => parentRoute,
    path: "dispatch/new",
    validateSearch: validateFromRequestSearch,
    component: DispatchNewJobPage,
  });
  const requests = createRoute({
    getParentRoute: () => parentRoute,
    path: "dispatch/requests",
    validateSearch: validateRequestsSearch,
    component: DispatchRequestsPage,
  });
  const requestNew = createRoute({
    getParentRoute: () => parentRoute,
    path: "dispatch/requests/new",
    component: DispatchNewRequestPage,
  });
  const requestOne = createRoute({
    getParentRoute: () => parentRoute,
    path: "dispatch/requests/$requestId",
    beforeLoad: ({ params }) => {
      if (!isRecordId((params as { requestId?: string }).requestId)) throw notFound();
    },
    notFoundComponent: RecordNotFound,
    component: DispatchRequestPage,
  });
  const signoffPeople = createRoute({
    getParentRoute: () => parentRoute,
    path: `dispatch/${SIGNOFF_PEOPLE.path}`,
    component: SIGNOFF_PEOPLE.component,
  });
  const tickets = createRoute({
    getParentRoute: () => parentRoute,
    path: "dispatch/tickets",
    validateSearch: validateTicketsSearch,
    component: DispatchTicketsPage,
  });
  const one = createRoute({
    getParentRoute: () => parentRoute,
    path: "dispatch/$jobId",
    beforeLoad: ({ params }) => {
      if (!isRecordId((params as { jobId?: string }).jobId)) throw notFound();
    },
    notFoundComponent: RecordNotFound,
    component: DispatchJobPage,
  });
  const edit = createRoute({
    getParentRoute: () => parentRoute,
    path: "dispatch/$jobId/edit",
    beforeLoad: ({ params }) => {
      if (!isRecordId((params as { jobId?: string }).jobId)) throw notFound();
    },
    notFoundComponent: RecordNotFound,
    component: DispatchEditJobPage,
  });
  return [list, create, signoffPeople, tickets, requests, requestNew, requestOne, edit, one];
};
